// src/features/reservations/lib/reservationFilters.ts
//
// Filters of «Reservas»: their state, the label of each value (shared by the
// select and its active-filter chip) and their round trip through the URL
// query string, so a filtered view can be shared.
import { DASH, formatMoney } from '@/lib/analyticsFormat';
import type { LiveChannel, ReservationPrimaryState, ReservationsSummary } from '@/types/reservationState';

// One label per filter value, shared by the select and its active-filter chip
// (the chip used to print the raw value: «Pre-check-in: done»).
// «A medias» is the stored pre-check-in state `started`; `partial` is accepted from older shared links.
export const PRE_CHECK_IN_LABEL_KEYS: Record<Exclude<FiltersState['preCheckIn'], ''>, string> = {
  done: 'resources.reservations.filters.preCheckInDone',
  started: 'resources.reservations.filters.preCheckInPartial',
  none: 'resources.reservations.filters.preCheckInNone',
};
// No «Pendiente»: a payment is paid, failed or absent — nothing is ever stored as pending.
export const PAYMENT_LABEL_KEYS: Record<Exclude<FiltersState['payment'], ''>, string> = {
  paid: 'resources.reservations.filters.paymentPaid',
  failed: 'resources.reservations.filters.paymentFailed',
  none: 'resources.reservations.filters.paymentNone',
};
export const KEY_LABEL_KEYS: Record<Exclude<FiltersState['key'], ''>, string> = {
  card: 'resources.reservations.filters.keyCard',
  digital: 'resources.reservations.filters.keyDigital',
  none: 'resources.reservations.filters.keyNone',
};

export interface FiltersState {
  axis: 'arrival' | 'activity';
  from: string;
  to: string;
  state: ReservationPrimaryState[];
  channelUsed: LiveChannel[];
  preCheckIn: '' | 'done' | 'started' | 'none';
  payment: '' | 'paid' | 'none' | 'failed';
  key: '' | 'card' | 'digital' | 'none';
  kioskLeftMidway: boolean;
  needsHelp: boolean;
  isWalkIn: boolean;
  origin: string;
  search: string;
}

export const DEFAULT_FILTERS: FiltersState = {
  axis: 'arrival',
  from: '',
  to: '',
  state: [],
  channelUsed: [],
  preCheckIn: '',
  payment: '',
  key: '',
  kioskLeftMidway: false,
  needsHelp: false,
  isWalkIn: false,
  origin: '',
  search: '',
};

const oneOf = <T extends string>(value: string | null, allowed: Record<T, string>): T | '' =>
  value && value in allowed ? (value as T) : '';

export const filtersFromSearchParams = (params: URLSearchParams): FiltersState => ({
  axis: (params.get('axis') as 'arrival' | 'activity') || 'arrival',
  from: params.get('from') || '',
  to: params.get('to') || '',
  state: (params.get('state') || '').split(',').filter(Boolean) as ReservationPrimaryState[],
  channelUsed: (params.get('channelUsed') || '').split(',').filter(Boolean) as LiveChannel[],
  preCheckIn: oneOf(params.get('preCheckIn') === 'partial' ? 'started' : params.get('preCheckIn'), PRE_CHECK_IN_LABEL_KEYS),
  payment: oneOf(params.get('payment'), PAYMENT_LABEL_KEYS),
  key: (params.get('key') as FiltersState['key']) || '',
  kioskLeftMidway: params.get('kioskLeftMidway') === 'true',
  needsHelp: params.get('needsHelp') === 'true',
  isWalkIn: params.get('isWalkIn') === 'true',
  origin: params.get('origin') || '',
  search: params.get('search') || '',
});

export const filtersToSearchParams = (filters: FiltersState): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.axis !== 'arrival') params.set('axis', filters.axis);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.state.length) params.set('state', filters.state.join(','));
  if (filters.channelUsed.length) params.set('channelUsed', filters.channelUsed.join(','));
  if (filters.preCheckIn) params.set('preCheckIn', filters.preCheckIn);
  if (filters.payment) params.set('payment', filters.payment);
  if (filters.key) params.set('key', filters.key);
  if (filters.kioskLeftMidway) params.set('kioskLeftMidway', 'true');
  if (filters.needsHelp) params.set('needsHelp', 'true');
  if (filters.isWalkIn) params.set('isWalkIn', 'true');
  if (filters.origin) params.set('origin', filters.origin);
  if (filters.search) params.set('search', filters.search);
  return params;
};

/**
 * «Cobrado» of the summary (P03, as in Cobros: firm charges minus refunds): one
 * amount per currency, never added across currencies; «—» when a paid
 * reservation of the set has no transaction detail to read.
 */
export const chargedSummary = (summary: ReservationsSummary): string | null => {
  if (summary.chargedMeasurable === false) return DASH;
  if (summary.chargedByCurrency) {
    return summary.chargedByCurrency.length === 0
      ? formatMoney(0, summary.chargedCurrency || 'EUR')
      : summary.chargedByCurrency.map((c) => formatMoney(c.amount, c.currency)).join(' · ');
  }
  return summary.charged != null ? formatMoney(summary.charged, summary.chargedCurrency || 'EUR') : null;
};
